import { ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal, inject } from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { DropdownColumnsActionsComponent } from '@shared/components/trading/widgets/common/dropdown-columns-actions/dropdown-columns-actions.component'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { ColumnsOptionsOpenedOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/options/columns-options-opened-orders.enum'
import { ColumnsOptionsCanceledOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/options/columns-options-canceled-orders.enum'
import { ColumnsOptionsExecutedOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/options/columns-options-executed-orders.enum'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-dropdown-columns-orders-options',
	templateUrl: './dropdown-columns-orders-options.component.html',
	styleUrl: './dropdown-columns-orders-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DropdownColumnsActionsComponent],
})
export class DropdownColumnsOrdersOptionsComponent {
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)

	protected openColumns: Signal<ColumnInterface<ColumnsOptionsOpenedOrdersEnum>[]> = computed<
		ColumnInterface<ColumnsOptionsOpenedOrdersEnum>[]
	>((): ColumnInterface<ColumnsOptionsOpenedOrdersEnum>[] => {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()

		return widget?.settings.options.orders.open.columns ?? []
	})

	protected canceledColumns: Signal<ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[]> = computed<
		ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[]
	>((): ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[] => {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()

		return widget?.settings.options.orders.canceled.columns ?? []
	})

	protected filledColumns: Signal<ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[]> = computed<
		ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[]
	>((): ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[] => {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()

		return widget?.settings.options.orders.filled.columns ?? []
	})
	protected onChangeOpenColumns(columns: ColumnInterface<ColumnsOptionsOpenedOrdersEnum>[]): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...widget.settings.options,
					orders: {
						...widget.settings.options.orders,
						open: { columns },
					},
				},
			},
		})
	}

	protected onChangeCanceledColumns(columns: ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[]): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...widget.settings.options,
					orders: {
						...widget.settings.options.orders,
						canceled: { columns },
					},
				},
			},
		})
	}

	protected onChangeFilledColumns(columns: ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[]): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...widget.settings.options,
					orders: {
						...widget.settings.options.orders,
						filled: { columns },
					},
				},
			},
		})
	}
}
